import React, { useState, useEffect } from 'react';
import { Search, Plus, Eye, EyeOff, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getData, postData, serverURL } from '../../../../Services/Fetchnodeservices';

export default function RestaurantDashboard() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [menuFilter, setMenuFilter] = useState('All'); // New: All, ON, OFF
  const [visibleRows, setVisibleRows] = useState({});
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuLoading, setMenuLoading] = useState(null);
  const [stats, setStats] = useState({
    totalMenus: 0,
    totalItems: 0,
    activeMenus: 0,
    pending: 0
  });

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getData("Menu/get_restaurant_settings");
      console.log("=== RESTAURANT SETTINGS RESPONSE ===");
      console.log("Full Response:", response);
      
      if (response && response.status) {
        console.log("✅ Successfully fetched restaurant settings");
        console.log("📊 Data Count:", response.data?.length || 0);
        console.log("📝 Data:", response.data);
        
        if (response.data && response.data.length > 0) {
          const transformedData = response.data.map(item => ({
            id: item.id,
            name: item.organisation_name || 'N/A',
            contact: item.contact || 'N/A',
            items: item.item_count || 0,
            direction: getDirectionText(item),
            commission: getCommissionText(item),
            status: Number(item.status) === 1 ? "Active" : Number(item.status) === 0 ? "Inactive" : "",
            is_menu: item.is_menu || 0,
            updated: item.updated_at ? new Date(item.updated_at).toLocaleString() : 'N/A',
            menu_direction: item.menu_direction,
            menu_direction_type: item.menu_direction_type,
            menu_direction_value: item.menu_direction_value,
            comission: item.comission,
            special_comission: item.special_comission
          }));
          
          setRestaurants(transformedData);
          
          const totalMenus = transformedData.length;
          const totalItems = transformedData.reduce((sum, item) => sum + item.items, 0);
          const activeMenus = transformedData.filter(item => item.status === 'Active').length;
          const pending = transformedData.filter(item => item.status === 'Inactive').length;
          
          setStats({
            totalMenus,
            totalItems,
            activeMenus,
            pending
          });
          
          console.log("📊 Transformed Data:", transformedData);
        } else {
          console.log("⚠️ No restaurant data found");
          setRestaurants([]);
          setStats({
            totalMenus: 0,
            totalItems: 0,
            activeMenus: 0,
            pending: 0
          });
        }
      } else {
        console.log("❌ Failed to fetch restaurant settings");
        console.log("Error Message:", response?.message || 'Unknown error');
        setError(response?.message || 'Failed to fetch data');
        setRestaurants([]);
      }
    } catch (error) {
      console.error("❌ Error fetching restaurant settings:", error);
      console.error("Error Details:", error.message);
      if (error.response) {
        console.error("Server Response:", error.response.data);
      }
      setError(error.message || 'Server error while fetching data');
      setRestaurants([]);
    } finally {
      setLoading(false);
      console.log("=== END OF FETCH ===");
    }
  };

  const getDirectionText = (item) => {
    if (!item.is_menu || item.is_menu === 0) {
      return 'Neutral';
    }
    if (item.menu_direction_type === 'Percentage') {
      return `${item.menu_direction_value || 0}%`;
    }
    return item.menu_direction || 'Neutral';
  };

  const getCommissionText = (item) => {
    const comission = item.comission || 0;
    const specialComission = item.special_comission || 0;
    return `${comission}% (Sp: ${specialComission}%)`;
  };

  const handleMenuToggle = async (id, currentStatus) => {
    const currentNum = Number(currentStatus);
    const newStatus = currentNum === 1 ? 0 : 1;
    
    console.log(`🔄 Toggling: ID ${id}, ${currentNum} -> ${newStatus}`);
    
    setRestaurants(prev => 
      prev.map(item => 
        item.id === id 
          ? { ...item, is_menu: newStatus, _updating: true }
          : item
      )
    );
    
    setMenuLoading(id);
    
    try {
      const response = await postData("Menu/change_menu_status", {
        id: id,
        is_menu: newStatus
      });
      
      console.log("📥 Response:", response);
      
      if (response && response.status === true) {
        setRestaurants(prev => 
          prev.map(item => 
            item.id === id 
              ? { ...item, is_menu: newStatus, _updating: false }
              : item
          )
        );
        
        await fetchRestaurants();
        console.log(`✅ Menu ${newStatus === 1 ? 'Enabled' : 'Disabled'}`);
      } else {
        console.log("❌ Failed, reverting...");
        setRestaurants(prev => 
          prev.map(item => 
            item.id === id 
              ? { ...item, is_menu: currentNum, _updating: false }
              : item
          )
        );
        alert(response?.message || 'Failed to change menu status');
      }
    } catch (error) {
      console.error("❌ Error:", error);
      setRestaurants(prev => 
        prev.map(item => 
          item.id === id 
            ? { ...item, is_menu: currentNum, _updating: false }
            : item
        )
      );
      alert('Server error while changing menu status');
    } finally {
      setMenuLoading(null);
    }
  };

  const handleViewRestaurant = (restaurant) => {
    console.log("👀 Navigating to menu with:", {
      designation_id: restaurant.id,
      restaurantName: restaurant.name
    });
    
    navigate('/Dashboard/Menus', {
      state: {
        designation_id: restaurant.id,
        restaurantName: restaurant.name,
        restaurantData: restaurant
      }
    });
  };

  // Function to get first two letters for avatar
  const getInitials = (name) => {
    if (!name || name === 'N/A') return '??';
    const words = name.trim().split(' ');
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  };

  // Generate random but consistent color for each restaurant
  const getAvatarColor = (id) => {
    const colors = [
      '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', 
      '#10b981', '#06b6d4', '#ef4444', '#14b8a6',
      '#f97316', '#6366f1', '#84cc16', '#0ea5e9'
    ];
    return colors[id % colors.length];
  };

  // Updated filter logic with menu filter
  const filteredRestaurants = restaurants.filter(restaurant => {
    const matchesSearch = restaurant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         restaurant.contact.includes(searchTerm) ||
                         restaurant.id.toString().includes(searchTerm);
    
    const matchesStatus = statusFilter === 'All' || restaurant.status === statusFilter;
    
    // Menu filter: All, ON (is_menu === 1), OFF (is_menu === 0)
    let matchesMenu = true;
    if (menuFilter === 'ON') {
      matchesMenu = restaurant.is_menu === 1;
    } else if (menuFilter === 'OFF') {
      matchesMenu = restaurant.is_menu === 0;
    }
    
    return matchesSearch && matchesStatus && matchesMenu;
  });

  const StatCard = ({ icon, label, value }) => (
    <div style={{
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      padding: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    }}>
      <div style={{ fontSize: '24px' }}>{icon}</div>
      <div>
        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#111827' }}>{value}</div>
        <div style={{ fontSize: '12px', color: '#6b7280' }}>{label}</div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader className="animate-spin" size={48} color="#3B82F6" style={{ margin: '0 auto 16px', display: 'block' }} />
          <p style={{ color: '#6b7280' }}>Loading restaurants...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          textAlign: 'center',
          backgroundColor: '#ffffff',
          padding: '32px',
          borderRadius: '12px',
          boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
          maxWidth: '448px'
        }}>
          <div style={{ color: '#ef4444', fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', marginBottom: '8px' }}>Error Loading Data</h3>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>{error}</p>
          <button
            onClick={fetchRestaurants}
            style={{
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              fontWeight: '600',
              padding: '8px 24px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      padding: '16px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header Stats - More Compact */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '10px',
          marginBottom: '16px'
        }}>
          <StatCard icon="🏪" label="Total Menus" value={stats.totalMenus} />
          <StatCard icon="🍽️" label="Total Items" value={stats.totalItems} />
          <StatCard icon="📈" label="Active Menus" value={stats.activeMenus} />
          <StatCard icon="⏰" label="Pending" value={stats.pending} />
        </div>

        {/* Action Button - Smaller */}
        <div style={{ marginBottom: '16px' }}>
          <button style={{
            backgroundColor: '#3b82f6',
            color: '#ffffff',
            fontWeight: '600',
            padding: '6px 16px',
            borderRadius: '6px',
            border: 'none',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
          >
            <Plus size={16} />
            Initialize Menu
          </button>
        </div>

        {/* Search and Filter Bar - Complete */}
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '6px',
          border: '1px solid #e5e7eb',
          padding: '12px 16px',
          marginBottom: '16px'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '12px',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            {/* Search Bar */}
            <div style={{
              flex: 1,
              position: 'relative',
              minWidth: '180px',
              maxWidth: '300px'
            }}>
              <Search style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af'
              }} size={16} />
              <input
                type="text"
                placeholder="Search restaurants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 12px 6px 34px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  outline: 'none',
                  fontSize: '13px',
                  height: '34px'
                }}
                onFocus={(e) => e.target.style.outline = '2px solid #3b82f6'}
                onBlur={(e) => e.target.style.outline = 'none'}
              />
            </div>

            {/* Filter Group Label */}
            <span style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#6b7280',
              marginRight: '4px'
            }}>
              Status:
            </span>

            {/* Status Filter Buttons */}
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {['All', 'Active', 'Inactive'].map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  style={{
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontWeight: '500',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    backgroundColor: statusFilter === status ? '#3b82f6' : '#e5e7eb',
                    color: statusFilter === status ? '#ffffff' : '#374151',
                    fontSize: '12px',
                    height: '30px'
                  }}
                >
                  {status}
                </button>
              ))}
            </div>

            {/* Divider */}
            <div style={{
              width: '1px',
              height: '28px',
              backgroundColor: '#e5e7eb'
            }} />

            {/* Menu Filter Label */}
            <span style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#6b7280',
              marginRight: '4px'
            }}>
              Menu:
            </span>

            {/* Menu Filter Buttons */}
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {['All', 'ON', 'OFF'].map(option => (
                <button
                  key={option}
                  onClick={() => setMenuFilter(option)}
                  style={{
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontWeight: '500',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    backgroundColor: menuFilter === option ? '#10b981' : '#e5e7eb',
                    color: menuFilter === option ? '#ffffff' : '#374151',
                    fontSize: '12px',
                    height: '30px'
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Restaurant Table - Compact with no horizontal scroll */}
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          overflow: 'hidden'
        }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            tableLayout: 'fixed'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ width: '28px', padding: '8px 6px', textAlign: 'center' }}>
                  <input type="checkbox" style={{ borderRadius: '3px', width: '14px', height: '14px' }} />
                </th>
                <th style={{ width: '36px', padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: '#374151' }}>ID</th>
                <th style={{ width: '36px', padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: '#374151' }}>Pic</th>
                <th style={{ width: '120px', padding: '8px 6px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#374151' }}>Restaurant</th>
                <th style={{ width: '70px', padding: '8px 6px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#374151' }}>Contact</th>
                <th style={{ width: '44px', padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: '#374151' }}>Items</th>
                <th style={{ width: '55px', padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: '#374151' }}>Direction</th>
                <th style={{ width: '75px', padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: '#374151' }}>Commission</th>
                <th style={{ width: '65px', padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: '#374151' }}>Menu</th>
                <th style={{ width: '55px', padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: '#374151' }}>Status</th>
                <th style={{ width: '70px', padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: '#374151' }}>Updated</th>
                <th style={{ width: '45px', padding: '8px 6px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: '#374151' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRestaurants.length > 0 ? (
                filteredRestaurants.map((restaurant, idx) => {
                  const isUpdating = restaurant._updating || menuLoading === restaurant.id;
                  const initials = getInitials(restaurant.name);
                  const avatarColor = getAvatarColor(restaurant.id);
                  
                  return (
                    <tr key={restaurant.id} style={{ 
                      backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb',
                      opacity: isUpdating ? 0.7 : 1,
                      transition: 'opacity 0.3s ease'
                    }}>
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                        <input type="checkbox" style={{ borderRadius: '3px', width: '14px', height: '14px' }} />
                      </td>
                      <td style={{ padding: '6px 6px', fontSize: '11px', fontWeight: '500', color: '#111827', textAlign: 'center' }}>{restaurant.id}</td>
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                        <div style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${avatarColor}, ${avatarColor}dd)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '11px',
                          fontWeight: '700',
                          color: '#ffffff',
                          margin: '0 auto',
                          textTransform: 'uppercase',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          {initials}
                        </div>
                      </td>
                      <td style={{ 
                        padding: '6px 6px', 
                        fontSize: '12px', 
                        fontWeight: '500', 
                        color: '#111827', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        maxWidth: '120px'
                      }}>
                        <span title={restaurant.name}>{restaurant.name}</span>
                      </td>
                      <td style={{ padding: '6px 6px', fontSize: '11px', color: '#6b7280' }}>{restaurant.contact}</td>
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '1px 6px',
                          borderRadius: '9999px',
                          fontSize: '11px',
                          fontWeight: '600',
                          backgroundColor: restaurant.items > 0 ? '#ccfbf1' : '#f3f4f6',
                          color: restaurant.items > 0 ? '#065f46' : '#6b7280',
                          minWidth: '20px'
                        }}>
                          {restaurant.items}
                        </span>
                      </td>
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                        {restaurant.items === 0 ? (
                          <span style={{ color: '#6b7280', fontSize: '11px' }}>−</span>
                        ) : (
                          <span style={{ color: '#0d9488', fontWeight: '500', fontSize: '10px' }}>{restaurant.direction}</span>
                        )}
                      </td>
                      <td style={{ padding: '6px 6px', fontSize: '10px', color: '#6b7280', textAlign: 'center' }}>{restaurant.commission}</td>
                      
                      {/* Menu Toggle - Compact */}
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                          <button
                            onClick={() => handleMenuToggle(restaurant.id, restaurant.is_menu)}
                            disabled={isUpdating}
                            style={{
                              position: 'relative',
                              width: '38px',
                              height: '20px',
                              borderRadius: '10px',
                              border: 'none',
                              cursor: isUpdating ? 'not-allowed' : 'pointer',
                              background: restaurant.is_menu === 1 ? '#1D9E75' : '#d1d5db',
                              transition: 'background 0.25s ease',
                              padding: 0,
                              opacity: isUpdating ? 0.55 : 1,
                              flexShrink: 0
                            }}
                          >
                            <span
                              style={{
                                position: 'absolute',
                                top: '2px',
                                left: '2px',
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                background: '#ffffff',
                                transform: restaurant.is_menu === 1 ? 'translateX(18px)' : 'translateX(0)',
                                transition: 'transform 0.25s ease',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '8px'
                              }}
                            >
                              {isUpdating
                                ? <Loader size={8} className="animate-spin" color="#6b7280" />
                                : restaurant.is_menu === 1
                                  ? <span style={{ color: '#1D9E75', fontWeight: 700 }}>✓</span>
                                  : <span style={{ color: '#9ca3af', fontWeight: 700 }}>✕</span>
                              }
                            </span>
                          </button>
                          <span style={{
                            fontSize: '10px',
                            fontWeight: 500,
                            minWidth: '18px',
                            color: isUpdating ? '#9ca3af' : restaurant.is_menu === 1 ? '#1D9E75' : '#9ca3af'
                          }}>
                            {isUpdating ? '...' : restaurant.is_menu === 1 ? 'ON' : 'OFF'}
                          </span>
                        </div>
                      </td>
                      
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '1px 6px',
                          borderRadius: '9999px',
                          fontSize: '10px',
                          fontWeight: '600',
                          backgroundColor: restaurant.status === 'Active' ? '#dcfce7' : '#fee2e2',
                          color: restaurant.status === 'Active' ? '#166534' : '#991b1b'
                        }}>
                          {restaurant.status === 'Active' ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '6px 6px', fontSize: '9px', color: '#6b7280', textAlign: 'center' }}>
                        {restaurant.updated}
                      </td>
                      
                      <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleViewRestaurant(restaurant)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#3b82f6',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            transition: 'all 0.2s',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '2px',
                            fontSize: '11px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#eff6ff';
                            e.target.style.color = '#2563eb';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = '#3b82f6';
                          }}
                        >
                          <Eye size={13} />
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="12" style={{ padding: '32px 24px', textAlign: 'center', color: '#6b7280' }}>
                    <div style={{ fontSize: '32px', marginBottom: '6px' }}>📭</div>
                    <p style={{ fontSize: '13px' }}>No restaurants found</p>
                    <p style={{ fontSize: '12px', marginTop: '2px' }}>Try adjusting your search or filters</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Results Count */}
        <div style={{ marginTop: '10px', fontSize: '12px', color: '#6b7280' }}>
          Showing {filteredRestaurants.length} of {restaurants.length} restaurants
        </div>
      </div>
    </div>
  );
}